import { useBranchStore } from '@/store/branchStore';
import {
  DAYS_ORDER,
  capitalizeFirstLetter,
  formatTime,
} from '@/utils/dateTime';
import {
  Facebook01Icon,
  InstagramIcon,
  Linkedin01Icon,
  TwitterIcon,
  YoutubeIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { Link } from 'react-router-dom';
import { Badge } from './ui/badge';

export function Footer() {
  const branchInfo = useBranchStore(s => s.branchInfo);
  const branchLoading = useBranchStore(s => s.branchLoading);
  const getBranchName = useBranchStore(s => s.getBranchName);

  if (branchLoading && !branchInfo) {
    return null;
  }

  const footerTextColor = 'text-[var(--footer-text-color)]';
  const hoverUnderlineAnimation =
    'relative inline-block after:absolute after:bottom-0 after:left-0 after:h-[1px] after:w-0 after:bg-current after:transition-all after:duration-200 after:ease-out-cubic hover:after:w-full';

  const pharmacyName = getBranchName() || 'Pharmacare';
  const phoneNumber = branchInfo?.phone_number
    ? `${branchInfo.dial_code}${branchInfo.phone_number}`
    : '+234 901 234 5678';
  const address = branchInfo
    ? `${branchInfo.address}, ${branchInfo.lga}, ${branchInfo.state}`
    : '123 Healthcare Avenue, Victoria Island, Lagos, Nigeria';

  const workHoursMap = new Map<string, { open: string; close: string }[]>();

  if (branchInfo?.workHours) {
    branchInfo.workHours.forEach(
      (wh: { day: string; open: string; close: string }) => {
        const day = wh.day.toLowerCase();
        const openTimes = wh.open.split(',').map(t => t.trim());
        const closeTimes = wh.close.split(',').map(t => t.trim());

        const hours = openTimes.map((open, idx) => ({
          open,
          close: closeTimes[idx] || closeTimes[0],
        }));

        workHoursMap.set(day, hours);
      }
    );
  }

  const summarizeHours = () => {
    const groups: {
      start: string;
      end: string;
      hours: string;
    }[] = [];

    let startDay: string | null = null;
    let currentHours: string | null = null;
    let previousDay: string | null = null;

    DAYS_ORDER.forEach(day => {
      const hours = workHoursMap.get(day);
      const hoursText = hours
        ? hours
            .map(h => `${formatTime(h.open)} - ${formatTime(h.close)}`)
            .join(', ')
        : 'Closed';

      if (currentHours === hoursText) {
        previousDay = day;
      } else {
        if (startDay && previousDay) {
          groups.push({
            start: startDay,
            end: previousDay,
            hours: currentHours!,
          });
        }
        startDay = day;
        currentHours = hoursText;
        previousDay = day;
      }
    });

    if (startDay && previousDay) {
      groups.push({
        start: startDay,
        end: previousDay,
        hours: currentHours!,
      });
    }

    return groups
      .map(group => {
        const startLabel = capitalizeFirstLetter(group.start);
        const endLabel = capitalizeFirstLetter(group.end);
        const dayPart =
          group.start === group.end
            ? startLabel
            : `${startLabel} - ${endLabel}`;
        return `${dayPart} ${group.hours}`;
      })
      .join(', ');
  };

  const summarizedHours = summarizeHours();

  return (
    <footer className="bg-primary w-full shadow-footer">
      <div className="container mx-auto py-16 h-fit flex flex-col gap-[72px]">
        <div className="flex flex-col md:flex-row gap-[48px] w-full">
          <div className="flex flex-col gap-3 flex-1 min-w-[180px]">
            <span
              className={`uppercase font-bold text-[14px] leading-5 tracking-wide font-geist ${footerTextColor}`}
            >
              Contact
            </span>
            <div className="flex flex-col gap-3 mt-1">
              <span className={`bodysmall ${footerTextColor}`}>
                Phone:{' '}
                <a href={`tel:${phoneNumber}`} className="underline">
                  {phoneNumber}
                </a>
              </span>

              <span className={`bodysmall ${footerTextColor}`}>
                Hours: {summarizedHours}
              </span>

              <span className={`bodysmall ${footerTextColor}`}>
                Address: {address}
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-3 flex-1 md:max-w-[180px]">
            <span
              className={`uppercase font-bold text-[14px] leading-5 tracking-wide font-geist ${footerTextColor}`}
            >
              Company
            </span>
            <div className="flex flex-col gap-3 mt-1 w-fit">
              <Link
                to="/"
                className={`bodysmall ${footerTextColor} ${hoverUnderlineAnimation}`}
              >
                Home
              </Link>
              <Link
                to="/about"
                className={`bodysmall ${footerTextColor} ${hoverUnderlineAnimation}`}
              >
                About Us
              </Link>
            </div>
          </div>

          <div className="flex flex-col gap-3 flex-1 md:max-w-[180px]">
            <span
              className={`uppercase font-bold text-[14px] leading-5 tracking-wide font-geist ${footerTextColor}`}
            >
              Legal
            </span>
            <div className="flex flex-col gap-3 mt-1 w-fit">
              <Link
                to="/privacy-policy"
                className={`bodysmall ${footerTextColor} ${hoverUnderlineAnimation}`}
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms-of-use"
                className={`bodysmall ${footerTextColor} ${hoverUnderlineAnimation}`}
              >
                Terms of Use
              </Link>
              <Link
                to="/refund-policy"
                className={`bodysmall ${footerTextColor} ${hoverUnderlineAnimation}`}
              >
                Refund Policy
              </Link>
            </div>
          </div>
        </div>

        <div className="w-full border-t border-gray-100" />

        <div className="flex flex-col gap-3 md:gap-8">
          <div className="flex flex-col md:flex-row w-full justify-center md:justify-between items-center gap-8 md:gap-0">
            <span className={footerTextColor}>
              © 2024 {pharmacyName}. All rights reserved.
            </span>

            <div className="flex flex-wrap gap-5 items-center justify-center md:justify-start w-full md:w-auto">
              <a
                href="#"
                className={`flex items-center gap-1 font-geist ${footerTextColor}`}
              >
                <HugeiconsIcon icon={Linkedin01Icon} size={24} />
                LinkedIn
              </a>
              <a
                href="#"
                className={`flex items-center gap-1 font-geist ${footerTextColor}`}
              >
                <HugeiconsIcon icon={TwitterIcon} size={24} />
                Twitter
              </a>
              <a
                href="#"
                className={`flex items-center gap-1 font-geist ${footerTextColor}`}
              >
                <HugeiconsIcon icon={InstagramIcon} size={24} />
                Instagram
              </a>
              <a
                href="#"
                className={`flex items-center gap-1 font-geist ${footerTextColor}`}
              >
                <HugeiconsIcon icon={Facebook01Icon} size={24} />
                Facebook
              </a>
              <a
                href="#"
                className={`flex items-center gap-1 font-geist ${footerTextColor}`}
              >
                <HugeiconsIcon icon={YoutubeIcon} size={24} />
                Youtube
              </a>
            </div>
          </div>

          <div className="flex flex-col md:flex-row w-full justify-start items-center mt-2">
            <a
              href="https://www.famasi.me/dispensary"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Badge
                variant="secondary"
                className="text-[14px] font-geist tracking-wider px-3 py-1 font-medium rounded-full bg-[var(--footer-badge-bg)] text-white underline underline-offset-2 decoration-white border-none focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 transition-all duration-200 ease-out-cubic"
                aria-label="A Dispensary by Famasi website"
              >
                A Dispensary by Famasi website
              </Badge>
            </a>
            <a
              href="https://usedispensary.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Badge
                variant="secondary"
                className="text-[14px] font-geist tracking-wider px-3 py-1 font-medium rounded-full bg-[var(--footer-badge-bg)] text-white underline underline-offset-2 decoration-white border-none focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 transition-all duration-200 ease-out-cubic"
                aria-label="Create yours"
              >
                Create yours
              </Badge>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
